import { t } from 'i18next';
import { Box, Tooltip } from '@mui/material';
import { Button } from '@atoms';
import { parseAmount } from '@utils';
import { styles } from './styles';

export type NamelessCardRegistrationStatus =
  | 'pending_user'
  | 'failed_user'
  | 'pending_account'
  | 'failed_account'
  | 'pending_prepaid_card'
  | 'failed_prepaid_card'
  | 'pending_user_product'
  | 'failed_user_product'
  | 'pending_dispersion'
  | 'failed_dispersion'
  | 'pending_token'
  | 'failed_token'
  | 'processed';

export interface NamelessCardBulkDetailResult {
  id: string;
  namelessRegistrationId: string;
  status: NamelessCardRegistrationStatus;
  email: string;
  amount: number;
  userId: string;
  username: string;
  accountNumber: string;
  prepaidCardId: string;
  transactionId: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/**
 * Returns the label for a given registration status
 */
const getRegistrationStatusLabel = (status: NamelessCardRegistrationStatus) =>
  t(`GIFT_CARDS:REGISTRATION_STATUS.${status.toUpperCase()}`);

/**
 * Columns for the nameless card bulk detail results table
 */
export const getResultColumns = ({
  onRetry,
  retryingId,
}: {
  onRetry: (row: NamelessCardBulkDetailResult) => void;
  retryingId: string | null;
}) => [
  { title: 'COMMON:EMAIL', content: ({ email }: NamelessCardBulkDetailResult) => email },
  {
    title: 'DISPERSIONS:AMOUNT',
    content: ({ amount }: NamelessCardBulkDetailResult) => parseAmount(amount),
  },
  {
    title: 'GIFT_CARDS:RESULT',
    content: ({ status, description }: NamelessCardBulkDetailResult) => {
      const label = getRegistrationStatusLabel(status);
      const tooltipText = (status.startsWith('failed_') && description) || label;
      const isMaxCharacter = tooltipText.length > 25;

      return (
        <Tooltip title={isMaxCharacter ? tooltipText : ''} arrow>
          <Box sx={styles.rowEllipsis}>{label}</Box>
        </Tooltip>
      );
    },
  },
  {
    title: ' ',
    content: (row: NamelessCardBulkDetailResult) => {
      if (row.status !== 'failed_dispersion') return null;

      return (
        <Box sx={styles.retryButton}>
          <Button
            text='GIFT_CARDS:RETRY'
            variant='contained'
            color='primary'
            size='small'
            isLoading={retryingId === row.id}
            disabled={Boolean(retryingId) && retryingId !== row.id}
            onClick={() => onRetry(row)}
          />
        </Box>
      );
    },
  },
];
